<template>
  <a v-if="href" :href="href" class="btn" :class="setClasses">
    <slot />
  </a>
  <router-link v-else-if="type == 'router'" :to="cLink(to)" class="btn" :class="setClasses">
    <slot />
  </router-link>
  <button v-else :type="type" class="btn" :class="setClasses" @click="$emit('click')">
    <slot />
  </button>
</template>

<script>
import { logicMixin } from '@/mixins/LogicMixin';

export default {
  name: 'Button',
  mixins: [logicMixin],
  props: {
    appearance: {
      type: String,
      default: 'secondary'
    },
    size: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button'
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: String,
      default: null
    }
  },
  computed: {
    setClasses: function() {
      var classes = [];

      if (this.appearance) {
        classes.push('--is-' + this.appearance);
      }

      if (this.size) {
        classes.push('--is-' + this.size);
      }

      return classes.join(' ');
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * Button
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc LP Button
 * @since 2020-10-27
**/

// Variables
$component: 'btn';

// Variants
%primary {
  border-color: darken($primary, 2%);
  box-shadow: 0 1px 0 0 rgba(darken($primary, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, lighten($primary, 3%), $primary);
  color: $white;
}
%primary-hover {
  border-color: darken($primary, 4%);
  box-shadow: 0 1px 2px 0 rgba(darken($primary, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, $primary, darken($primary, 3%));
}
%primary-focus {
  outline: none;
}
%primary-active {
  border-color: darken($primary, 6%);
  background: darken($primary, 3%);
  box-shadow: inset 0 2px 1px 0 rgba(darken($primary, 10%), 0.54);
}
%primary-disabled {
  border-color: desaturate(lighten($primary, 20%), 25%);
  background: desaturate(lighten($primary, 25%), 25%);
  cursor: not-allowed;
}

%secondary {
  border-color: $sky-dark;
  box-shadow: 0 1px 0 0 rgba(22, 29, 37, 0.05);
  @include linear-gradient(to bottom, $white, $sky-lighter);
  color: $ink;
}
%secondary-hover {
  @include linear-gradient(to bottom, $sky-lighter, darken($sky-lighter, 3%));
}
%secondary-focus {
  outline: none;
}
%secondary-active {
  box-shadow: 0 0 0 0 transparent, inset 0 1px 1px 0 rgba($ink-lighter, 0.1),
    inset 0 1px 4px 0 rgba($ink-lighter, 0.2);
  background: $sky-light;
}
%secondary-disabled {
  border-color: $sky;
  background: $sky-lighter;
  color: $ink-lightest;
  cursor: not-allowed;
}

%special {
  border-color: darken(#FBDE62, 4%);
  box-shadow: 0 1px 0 0 rgba(22, 29, 37, 0.05);
  background: #FBDE62;
  color: #111;
}
%special-hover {
  background: darken(#FBDE62, 5%);
}
%special-focus {
  outline: none;
}
%special-active {
  background: darken(#FBDE62, 15%);
}
%special-disabled {
  border-color: $sky;
  background: $sky-lighter;
  color: $ink-lightest;
  cursor: not-allowed;
}

// Component
.#{$component} {
  display: inline-block;
  margin: 0;
  border: $btn-border-width solid transparent;
  @include padding-top(map-get($spacers, 3) / 1.5);
  @include padding-right(map-get($spacers, 4));
  @include padding-bottom(map-get($spacers, 3) / 1.5);
  @include padding-left(map-get($spacers, 4));
  border-radius: 50px;
  background-color: transparent;
  text-align: center;
  @include font-size(map-get($proposal-page, 'font-size-base'));
  line-height: $line-height-base;
  // transition: all ease-in-out 150ms;

  &.--is-sm {
    @include padding-top(map-get($spacers, 3) / 3);
    @include padding-right(map-get($spacers, 5) / 3);
    @include padding-bottom(map-get($spacers, 3) / 3);
    @include padding-left(map-get($spacers, 5) / 3);
    @include font-size(map-get($proposal-page, 'font-size-sm'));
  }

  &.--is-lg {
    @include font-size(map-get($proposal-page, 'font-size-lg'));
  }

  &.--is-primary {
    @extend %primary;

    &:hover {
      @extend %primary-hover;
    }

    &:focus {
      @extend %primary-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %primary-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %primary-disabled;
    }

    &.--is-active {
      @extend %primary-active;
    }
  }

  &.--is-secondary {
    @extend %secondary;

    &:hover {
      @extend %secondary-hover;
    }

    &:focus {
      @extend %secondary-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %secondary-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %secondary-disabled;
    }

    &.--is-active {
      @extend %secondary-active;
    }
  }

  &.--is-special {
    @extend %special;

    &:hover {
      @extend %special-hover;
    }

    &:focus {
      @extend %special-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %special-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %special-disabled;
    }

    &.--is-active {
      @extend %special-active;
    }
  }
}
</style>
